import { useBreakpoint } from "@brg/ui";
import { LayoutConst } from "core";
import { ReactNode, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useRouteMatch } from "react-router-dom";
import { countDuplicates } from "../support/countDuplicates";
import { LinksItems } from "../slideNavigation";
import { TFunctionDetailedResult } from "i18next";
import { AVATAR_SIZES } from "shared/constants/AvatarSizes";
import { useTranslation } from "react-i18next";
import { useTeamContext } from "shared/pages/exploreHseContextsNew/provider/TeamContext";

const INITIAL_ROW_COUNT = 10;
const DEFAULT_SORT_BAR_WIDTH = 300;
const EMPTY_SORT_WIDTH = 0;

interface ISlideSettingsProps {
    isDesigner?: boolean;
    isLoading?: boolean;
    activeTab?:string;
    isSuperPuperGames?: boolean;
    maxVisibleItems?: number;
    forceMobile?: boolean;
    items?: Record<string, ReactNode | string | LinksItems | TFunctionDetailedResult>;
}


export const useGetComputedSlides = ({
    items,
    maxVisibleItems,
    isDesigner,
    isLoading,
    activeTab,
    isSuperPuperGames,
    forceMobile,
}: ISlideSettingsProps) => {
    const langRef = useRef<any>(null);
    const { t, i18n: { language}} = useTranslation();
    const is992 = useBreakpoint(LayoutConst.screen.m);
    const isMobile = !!forceMobile || useBreakpoint(LayoutConst.screen.s-1)
    const is1200 = useBreakpoint(LayoutConst.screen.l)
    const isTeamOrCoursePage = !!useRouteMatch([/*'/teams', */'/courses']);
    const isTeamEdit = useRouteMatch('/team-edit');

    const { team } = useTeamContext();

    const [localLoading, setLocalLoading] = useState(false);
    const [rowCount, setRowCount] = useState(INITIAL_ROW_COUNT)
    const [sizes, setSizes] = useState<Record<string,number>>({})

    const gameSpecialCondition = isSuperPuperGames && is992 && !isMobile;

    const currentSearchBarWidth = useMemo(() => {
        return document.querySelector('.js-right-side-box')?.clientWidth || 0
    } , [/*dropdownRef?.current*/])

    const calculatedBarWidth = useMemo(
    () => {
      if ((isTeamEdit || (isTeamOrCoursePage && !isMobile))) {
        return EMPTY_SORT_WIDTH
      }
      if (team && team?.isSortingHidden) {
        return EMPTY_SORT_WIDTH
      }
      if (isDesigner) {
        return EMPTY_SORT_WIDTH
      }

      return currentSearchBarWidth || DEFAULT_SORT_BAR_WIDTH
    } 
    , [team, currentSearchBarWidth, isMobile])

    const maxRowCount = maxVisibleItems ?? (gameSpecialCondition ? 4 : rowCount);
    const cutLastTab = maxRowCount - 1

    const calcGap = useCallback(() => {
        if (isMobile) return 16;
        return 24;
    }, [isMobile])

    const calcRow = useCallback((maxWidth: number) => {
        const currentCountGap = (n: number) => calcGap() * (n - 1)
    
        let result = { overWidth: 0, lastRow: 0 };
    
        Object.entries(sizes).forEach(([item, width], idx, arr) => {
          if (result.lastRow > 0) return;
    
    
          if (((result.overWidth + (width as number)) + currentCountGap(idx)) > maxWidth) {
            result = { overWidth: result.overWidth, lastRow: idx - 1 };
            return;
          }
          result = { overWidth: result.overWidth += (width as number), lastRow: 0 };
        });

        if (result?.lastRow === 0 ) {
          return {...result, lastRow: Object.keys(sizes).length - 1}
        }
    
        return result;
      } , [sizes, calcGap])

      const onSetSizes = useCallback((props: { idx: string; width: number }) => {
        const { idx, width } = props;
      
        setSizes(prev => ({
          ...prev,
          [idx]: width
        }));
      }, []);

    const onSetRowCount = useCallback((data: any) => {
        if (data !== rowCount && !!data) {
          setRowCount(data)
        }
      } , [rowCount])

  const activeTabKey = useMemo(() => activeTab || null, [activeTab]);

  const finalItems = useMemo(() => {
    if (!items || isLoading || localLoading) return {};

    if (isMobile) return items;

    const allKeys = Object.keys(items);
    if (allKeys.length <= maxRowCount) return items;

    const activeIndex = activeTabKey ? allKeys.indexOf(activeTabKey) : -1;

    if (activeIndex >= maxRowCount) {
      const visibleKeys = [
        ...allKeys.slice(0, cutLastTab),
        activeTabKey!,
      ];

      return visibleKeys.reduce((acc, key) => {
        if (key && items[key]) acc[key] = items[key];
        return acc;
      }, {} as Record<string, any>);
    }

    return allKeys.slice(0, maxRowCount).reduce((acc, key) => {
      acc[key] = items[key];
      return acc;
    }, {} as Record<string, any>);
  }, [items, isMobile, isLoading, localLoading, maxRowCount, activeTabKey]);

  const finalDropdownItems = useMemo<Record<string, LinksItems> | null>(() => {
    if (!items || isLoading || localLoading || isMobile) return null;

    const allKeys = Object.keys(items);
    if (allKeys.length <= maxRowCount) return null;

    const visibleKeys = Object.keys(finalItems);

    return allKeys
      .filter((key) => !visibleKeys.includes(key))
      .reduce((acc, key) => {
        acc[key] = items[key] as LinksItems;
        return acc;
      }, {} as Record<string, LinksItems>);
  }, [items, isLoading, localLoading, isMobile, maxRowCount, finalItems]);

    useLayoutEffect(() => {
      if (localLoading) return
      const mobileSeachBarWidth = isTeamOrCoursePage && !isMobile ? 0 : calculatedBarWidth;

      const mainContainerWidth = !!is1200 ?  LayoutConst.container.xs : LayoutConst.container.l
      const maxContainerWidth = mainContainerWidth - mobileSeachBarWidth;

      
      const computeItems = Object.keys(finalItems);
      const allItems = Object.keys(items as Record<string,any>)
      const sizesInfoValues = Object.values(sizes);
      // Подсчитываем количество дублирующихся элементов в computeItems
      //const duplicateCount = countDuplicates(computeItems);
      // Новая длина sizesInfo, учитывающая дублирующиеся элементы
      //const adjustedSizesInfoLength = sizesInfoValues.length + duplicateCount;

      if ((allItems?.length >= computeItems.length) && !isLoading) {
          //const baseGap = calcGap() * adjustedSizesInfoLength;
          const baseGap = calcGap() * sizesInfoValues.length;
          const overWidth = sizesInfoValues.reduce((acc, val) => acc + val, 0);
          const baseValue = allItems && allItems?.length > 0 ? allItems?.length : INITIAL_ROW_COUNT;
          const needBreak = (overWidth + baseGap) > maxContainerWidth;

          if (!needBreak) {
              onSetRowCount(baseValue);
            return;
          }
      
          const { lastRow } = calcRow(maxContainerWidth);

          onSetRowCount(lastRow)
      }
    } , [
    calculatedBarWidth,
    isLoading,
    finalItems,
    localLoading,
    items,
    is1200,
    onSetRowCount,
    calcGap,
    calcRow,
    countDuplicates
    ])

    // useEffect(() => {
    //     //костыль для ресета размера элементов при смене языка
    //     if (langRef.current !== language) {
    //       setSizes({})
    //       setLocalLoading(true)
    //     }
    //     langRef.current = language
    //     setLocalLoading(false);
    //   } , [language])

    return {
        tabs: finalItems,
        dropdownTabs: finalDropdownItems,
        onSetSizes
    }
}import { useBreakpoint } from "@brg/ui";
import { LayoutConst } from "core";
import { ReactNode, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useRouteMatch } from "react-router-dom";
import { countDuplicates } from "../support/countDuplicates";
import { LinksItems } from "../slideNavigation";
import { TFunctionDetailedResult } from "i18next";
import { AVATAR_SIZES } from "shared/constants/AvatarSizes";
import { useTranslation } from "react-i18next";
import { useTeamContext } from "shared/pages/exploreHseContextsNew/provider/TeamContext";

const INITIAL_ROW_COUNT = 10;
const DEFAULT_SORT_BAR_WIDTH = 300;
const EMPTY_SORT_WIDTH = 0;

interface ISlideSettingsProps {
    isDesigner?: boolean;
    isLoading?: boolean;
    activeTab?:string;
    isSuperPuperGames?: boolean;
    maxVisibleItems?: number;
    forceMobile?: boolean;
    items?: Record<string, ReactNode | string | LinksItems | TFunctionDetailedResult>;
}


export const useGetComputedSlides = ({
    items,
    maxVisibleItems,
    isDesigner,
    isLoading,
    activeTab,
    isSuperPuperGames,
    forceMobile,
}: ISlideSettingsProps) => {
    const langRef = useRef<any>(null);
    const { t, i18n: { language}} = useTranslation();
    const is992 = useBreakpoint(LayoutConst.screen.m);
    const isMobile = !!forceMobile || useBreakpoint(LayoutConst.screen.s-1)
    const is1200 = useBreakpoint(LayoutConst.screen.l)
    const isTeamOrCoursePage = !!useRouteMatch([/*'/teams', */'/courses']);
    const isTeamEdit = useRouteMatch('/team-edit');

    const { team } = useTeamContext();

    const [localLoading, setLocalLoading] = useState(false);
    const [rowCount, setRowCount] = useState(INITIAL_ROW_COUNT)
    const [sizes, setSizes] = useState<Record<string,number>>({})

    const gameSpecialCondition = isSuperPuperGames && is992 && !isMobile;

    const currentSearchBarWidth = useMemo(() => {
        return document.querySelector('.js-right-side-box')?.clientWidth || 0
    } , [/*dropdownRef?.current*/])

    const calculatedBarWidth = useMemo(
    () => {
      if ((isTeamEdit || (isTeamOrCoursePage && !isMobile))) {
        return EMPTY_SORT_WIDTH
      }
      if (team && team?.isSortingHidden) {
        return EMPTY_SORT_WIDTH
      }
      if (isDesigner) {
        return EMPTY_SORT_WIDTH
      }

      return currentSearchBarWidth || DEFAULT_SORT_BAR_WIDTH
    } 
    , [team, currentSearchBarWidth, isMobile])

    const maxRowCount = maxVisibleItems ?? (gameSpecialCondition ? 4 : rowCount);
    const cutLastTab = maxRowCount - 1

    const calcGap = useCallback(() => {
        if (isMobile) return 16;
        return 24;
    }, [isMobile])

    const calcRow = useCallback((maxWidth: number) => {
        const currentCountGap = (n: number) => calcGap() * (n - 1)
    
        let result = { overWidth: 0, lastRow: 0 };
    
        Object.entries(sizes).forEach(([item, width], idx, arr) => {
          if (result.lastRow > 0) return;
    
    
          if (((result.overWidth + (width as number)) + currentCountGap(idx)) > maxWidth) {
            result = { overWidth: result.overWidth, lastRow: idx - 1 };
            return;
          }
          result = { overWidth: result.overWidth += (width as number), lastRow: 0 };
        });

        if (result?.lastRow === 0 ) {
          return {...result, lastRow: Object.keys(sizes).length - 1}
        }
    
        return result;
      } , [sizes, calcGap])

      const onSetSizes = useCallback((props: { idx: string; width: number }) => {
        const { idx, width } = props;
      
        setSizes(prev => ({
          ...prev,
          [idx]: width
        }));
      }, []);

    const onSetRowCount = useCallback((data: any) => {
        if (data !== rowCount && !!data) {
          setRowCount(data)
        }
      } , [rowCount])

  const activeTabKey = useMemo(() => activeTab || null, [activeTab]);

  const finalItems = useMemo(() => {
    if (!items || isLoading || localLoading) return {};

    if (isMobile) return items;

    const allKeys = Object.keys(items);
    if (allKeys.length <= maxRowCount) return items;

    const activeIndex = activeTabKey ? allKeys.indexOf(activeTabKey) : -1;

    if (activeIndex >= maxRowCount) {
      const visibleKeys = [
        ...allKeys.slice(0, cutLastTab),
        activeTabKey!,
      ];

      return visibleKeys.reduce((acc, key) => {
        if (key && items[key]) acc[key] = items[key];
        return acc;
      }, {} as Record<string, any>);
    }

    return allKeys.slice(0, maxRowCount).reduce((acc, key) => {
      acc[key] = items[key];
      return acc;
    }, {} as Record<string, any>);
  }, [items, isMobile, isLoading, localLoading, maxRowCount, activeTabKey]);

  const finalDropdownItems = useMemo<Record<string, LinksItems> | null>(() => {
    if (!items || isLoading || localLoading || isMobile) return null;

    const allKeys = Object.keys(items);
    if (allKeys.length <= maxRowCount) return null;

    const visibleKeys = Object.keys(finalItems);

    return allKeys
      .filter((key) => !visibleKeys.includes(key))
      .reduce((acc, key) => {
        acc[key] = items[key] as LinksItems;
        return acc;
      }, {} as Record<string, LinksItems>);
  }, [items, isLoading, localLoading, isMobile, maxRowCount, finalItems]);

    useLayoutEffect(() => {
      if (localLoading) return
      const mobileSeachBarWidth = isTeamOrCoursePage && !isMobile ? 0 : calculatedBarWidth;

      const mainContainerWidth = !!is1200 ?  LayoutConst.container.xs : LayoutConst.container.l
      const maxContainerWidth = mainContainerWidth - mobileSeachBarWidth;

      
      const computeItems = Object.keys(finalItems);
      const allItems = Object.keys(items as Record<string,any>)
      const sizesInfoValues = Object.values(sizes);
      // Подсчитываем количество дублирующихся элементов в computeItems
      //const duplicateCount = countDuplicates(computeItems);
      // Новая длина sizesInfo, учитывающая дублирующиеся элементы
      //const adjustedSizesInfoLength = sizesInfoValues.length + duplicateCount;

      if ((allItems?.length >= computeItems.length) && !isLoading) {
          //const baseGap = calcGap() * adjustedSizesInfoLength;
          const baseGap = calcGap() * sizesInfoValues.length;
          const overWidth = sizesInfoValues.reduce((acc, val) => acc + val, 0);
          const baseValue = allItems && allItems?.length > 0 ? allItems?.length : INITIAL_ROW_COUNT;
          const needBreak = (overWidth + baseGap) > maxContainerWidth;

          if (!needBreak) {
              onSetRowCount(baseValue);
            return;
          }
      
          const { lastRow } = calcRow(maxContainerWidth);

          onSetRowCount(lastRow)
      }
    } , [
    calculatedBarWidth,
    isLoading,
    finalItems,
    localLoading,
    items,
    is1200,
    onSetRowCount,
    calcGap,
    calcRow,
    countDuplicates
    ])

    // useEffect(() => {
    //     //костыль для ресета размера элементов при смене языка
    //     if (langRef.current !== language) {
    //       setSizes({})
    //       setLocalLoading(true)
    //     }
    //     langRef.current = language
    //     setLocalLoading(false);
    //   } , [language])

    return {
        tabs: finalItems,
        dropdownTabs: finalDropdownItems,
        onSetSizes
    }
}